import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { InvoiceData, LineItem } from "@/apis/gql/types";
import type { FormValues } from "@/components/shared/form/lineItems/cost-center";
import { CostCenterForm } from "@/components/shared/form/lineItems/cost-center";
import { TypographyLarge } from "@/components/ui/typography";
import { useTenant } from "@/providers/session";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";
import { useUpdateManyLineItems } from "@/queries/line-items/update";

type Props = {
  lineItems: LineItem[];
  disabled?: boolean;
};

export const InvoiceCostCenterForm = ({
  lineItems,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const updateLineItems = useUpdateManyLineItems();
  const { costCenters } = useGetAllCostCenters();
  const { hasCostCenters } = useTenant();

  const handleSubmit = async (formValues: FormValues) => {
    const { lineItemIds, ...rest } = formValues;
    await updateLineItems(lineItemIds, rest);
  };

  const initialValues = useMemo(
    () => ({
      costCenterId: lineItems[0]?.costCenterId ?? null,
      lineItemIds: lineItems.map((lineItem) => lineItem.lineItemId),
    }),
    [lineItems],
  );

  if (!hasCostCenters) {
    return null;
  }

  return (
    <>
      <TypographyLarge>{t("cost_centers.cost_centers")}</TypographyLarge>
      <CostCenterForm
        initialValues={initialValues}
        costCenters={costCenters}
        onSubmit={handleSubmit}
        readOnly={disabled}
      />
    </>
  );
};
